import React, { createContext, useContext } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/api'
import { useAuth } from './auth-context'
import { useOrg } from './org-context'

type Features = {
  career_pages: boolean
  career_pages_custom_domain: boolean
  opendb_search: boolean
  resdb_access: boolean
  resdb_ai_recommendations: boolean
  resdb_realtime_syncing: boolean
  talent_scout: boolean
  job_create: boolean
}

type SubscriptionData = {
  org_id: string
  user_id: string
  billing_type: 'admin-managed' | 'self-managed' | string // extend with known values if any
  plan: {
    plan_id: string
    title: string
    is_trial: boolean
    type: string
    current_term_end: Date
    status: string
    licenses: number
    team_size: number
    trial_used: boolean
  }
  features: Features
  active: boolean
  status: 'active' | 'inactive' | 'cancelled' | string // extend with other possible statuses/ ISO date string
}

type ApiResponse = {
  success: boolean
  data: SubscriptionData
}

interface SubscriptionContextType {
  subscription: SubscriptionData | null
  refetch: () => void
  isLoading: boolean
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null)

interface SubscriptionProviderProps {
  children: React.ReactNode
}

export function SubscriptionProvider({ children }: SubscriptionProviderProps) {
  const { user, loading: authLoading } = useAuth()
  const { currentOrg } = useOrg()

  // Stable conditions
  const userId = user?.user_id ?? null
  const orgId = currentOrg?.id ?? null

  const shouldEnable = !authLoading && !!userId && !!orgId

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['subscription', userId, orgId], // FIX #1
    queryFn: async () => {
      const res = await api.get<ApiResponse>('/recruiter/subscriptions/info')
      return res.data?.data
    },
    enabled: shouldEnable, // FIX #2
    refetchOnWindowFocus: false, // FIX #3
    refetchOnReconnect: false, // FIX #3
    refetchOnMount: false, // FIX #3
    staleTime: 1000 * 60, // avoid unnecessary calls
  })

  const value = React.useMemo(
    () => ({
      subscription: data || null,
      refetch,
      isLoading,
    }),
    [data, isLoading, refetch]
  )

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  )
}

export const useSubscription = () => {
  const context = useContext(SubscriptionContext)

  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider')
  }

  return context
}

export const useSubscriptionFeatures = (feature: keyof Features) => {
  const { subscription } = useSubscription()
  
  // Conditionally bypass subscription checks if the environment variable is set
  if (import.meta.env.VITE_BYPASS_SUBSCRIPTION === 'true') {
    return true
  }

  return subscription?.features[feature] || false
}

export const useSubscriptionStatus = () => {
  const { subscription } = useSubscription()
  
  // Conditionally bypass status check if the environment variable is set
  if (import.meta.env.VITE_BYPASS_SUBSCRIPTION === 'true') {
    return {
      isSubscriptionActive: true,
      plan: subscription?.plan,
    }
  }

  return {
    isSubscriptionActive: subscription?.active || false,
    plan: subscription?.plan,
  }
}
